import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@tremor/react";
import * as catalogAPI from "../../../services/catalog";
import CatalogCard from "./CatalogCard";
import { CUSTOM } from "./wizard";

export default function DeviceStep({ selected, onSelect, onBack }) {
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    if (!mountedRef.current) return;
    setStatus("loading");
    try {
      const data = await catalogAPI.list();
      if (mountedRef.current) {
        setEntries(data);
        setStatus("ready");
      }
    } catch (err) {
      if (mountedRef.current) {
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  return (
    <section>
      <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-50">Which device is it?</h2>
      <p className="mt-1 text-gray-600 dark:text-gray-400">
        Catalog devices come with a decoder, so readings show up with names and units.
      </p>

      {status === "loading" && <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">Loading devices…</p>}
      {status === "error" && (
        <div className="mt-6 flex items-center gap-3 rounded-lg bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          <span>We couldn't load the device catalog.</span>
          <Button size="xs" variant="secondary" onClick={load}>Retry</Button>
        </div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {entries.map((entry) => (
          <CatalogCard
            key={entry.id}
            entry={entry}
            selected={selected === entry.id}
            onSelect={() => onSelect(entry.id, entry)}
          />
        ))}
        <button
          type="button"
          aria-pressed={selected === CUSTOM}
          onClick={() => onSelect(CUSTOM, null)}
          className={`overflow-hidden rounded-xl border border-dashed bg-white dark:bg-gray-900 text-left ${
            selected === CUSTOM ? "border-blue-600 ring-2 ring-blue-100 dark:ring-blue-500/30" : "border-gray-300 dark:border-gray-800 hover:border-gray-400"
          }`}
        >
          <div className="flex h-28 items-end bg-gray-50 dark:bg-gray-800 px-5 pb-3">
            <span className="font-mono text-xs uppercase tracking-wider text-gray-600 dark:text-gray-400">Any LoRaWAN device</span>
          </div>
          <div className="px-5 py-4">
            <span className="block text-sm text-gray-500 dark:text-gray-400">My device isn't listed</span>
            <span className="block text-lg font-semibold text-gray-900 dark:text-gray-50">Custom (CayenneLPP)</span>
            <span className="block text-sm text-gray-600 dark:text-gray-400">Channels from your payload</span>
          </div>
        </button>
      </div>

      <div className="mt-6 flex justify-between">
        <Button variant="secondary" onClick={onBack}>Back</Button>
      </div>
    </section>
  );
}
